import type { AxiosError } from 'axios';
import { Box, Button, Center, Container, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type {
  Contract,
  ContractLine,
  PlannedItem,
  Space,
  WorkPackage,
  WorkPackageAssignee
} from '@/api/generated/models';
import type { UpdateWorkPackageRequestStatus } from '@/api/generated/models/updateWorkPackageRequestStatus';
import type { InfoField } from '@/components/common';
import type { SpaceLinePlanUsage } from '@/features/work-packages/workPackageDisplay';
import type { DraftSummary, DraftValue } from '@/utils/useItemsDraft';
import { useGetContract, useListContractLines } from '@/api/generated/contracts/contracts';
import { WorkPackageStatus } from '@/api/generated/models/workPackageStatus';
import { useListUnits } from '@/api/generated/units/units';
import {
  getGetWorkPackageQueryKey,
  getListAllWorkPackagesQueryKey,
  getListPlannedItemsByContractQueryKey,
  getListPlannedItemsQueryKey,
  getListWorkPackageAssigneesQueryKey,
  getListWorkPackagesByContractQueryKey,
  useDeleteWorkPackage,
  useGetSpace,
  useGetWorkPackage,
  useListPlannedItems,
  useListPlannedItemsByContract,
  useListSpaceLinePlansByContract,
  useListSpaces,
  useListWorkPackageAssignees,
  useListWorkPackagesByContract,
  useRemoveWorkPackageAssignee,
  useSavePlannedItems,
  useUpdateWorkPackage
} from '@/api/generated/work-structure/work-structure';
import { IconFigmaXCircle } from '@/assets/icons';
import {
  BackButton,
  CardSection,
  ConfirmActionModal,
  DRAFT_ROW_BACKGROUND,
  DraftNumberInput,
  InfoCard,
  ItemsSaveBar,
  ItemsSaveStatus,
  ListPagination,
  MobileActionBar,
  MutationButton,
  NewItemBadge,
  StatusBanner,
  StatusPill,
  StatusTracker,
  tableRowStyle,
  UndoRemoveButton,
  useUnsavedChangesGuard
} from '@/components/common';
import { InfoBulletList, PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';
import { WorkPackageAssigneePickerModal } from '@/features/work-packages/components/WorkPackageAssigneePickerModal';
import { WorkPackageItemPickerModal } from '@/features/work-packages/components/WorkPackageItemPickerModal';
import {
  buildSpaceBreadcrumb,
  buildWorkPackageTrackerSteps,
  collectDescendantSpaceIds,
  computeSpaceLinePlanUsage,
  invalidateContractSpaceUsage,
  linesPlannedInBranch,
  statusConfig
} from '@/features/work-packages/workPackageDisplay';
import { formatDate } from '@/utils/format';
import { useItemsDraft } from '@/utils/useItemsDraft';
import { useLocalPagination } from '@/utils/useLocalPagination';
import { useSavedMessage } from '@/utils/useSavedMessage';

const workPackageErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

/** Redak tablice stavki: spremljena stavka uz nespremljene promjene ili nova stavka iz nacrta. */
interface DisplayItem {
  key: string;
  itemId?: string;
  tempId?: string;
  contractLineId?: string;
  itemCode?: string;
  description?: string;
  unit?: string;
  isNew: boolean;
  isRemoved: boolean;
  plannedQty: DraftValue;
  savedQty: number;
  isChanged: boolean;
  error: string | null;
}

export const WorkPackageDetailPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const workPackageId = id ?? '';

  const [pickerOpened, setPickerOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [savedMessage, setSavedMessage] = useSavedMessage();
  // Specifično za brisanje/povratak u Nacrt — ako je blokirano evidentiranim aktivnostima, to je
  // trajnija informacija o stanju paketa, ne prolazna obavijest, pa ide kao banner na vrhu umjesto tosta.
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: workPackageData, isLoading } = useGetWorkPackage(workPackageId, {
    query: { enabled: !!workPackageId }
  });
  const workPackage = workPackageData as WorkPackage | undefined;

  // Upiti ovisni o učitanom radnom paketu (enabled) koriste staleTime 0, ne refetchOnMount:
  // uključe se tek nakon mounta, pa bi svjež keš (do 5 min) ostao — npr. ugovor ili razrada
  // promijenjeni s druge strane odmah nakon prihvaćanja.
  const { data: contractData } = useGetContract(workPackage?.contractId ?? '', {
    query: { enabled: !!workPackage?.contractId, staleTime: 0 }
  });
  const contract = contractData as Contract | undefined;

  const { data: spaceData } = useGetSpace(workPackage?.spaceId ?? '', {
    query: { enabled: !!workPackage?.spaceId }
  });
  const space = spaceData as Space | undefined;

  const { data: objectSpacesData } = useListSpaces(space?.objectId ?? '', {
    query: { enabled: !!space?.objectId, staleTime: 0 }
  });
  const objectSpaces: Space[] = objectSpacesData ?? [];
  const spaceBreadcrumb = buildSpaceBreadcrumb(workPackage?.spaceId, objectSpaces);

  const { data: plannedItemsData } = useListPlannedItems(workPackageId, {
    query: { enabled: !!workPackageId }
  });
  const plannedItems: PlannedItem[] = plannedItemsData ?? [];

  // Picker na "+ Dodaj stavke" smije nuditi SAMO artikle koji su preko razrade ugovora
  // (ContractSpaceBreakdown, contract_space_line_plans) već raspoređeni negdje unutar grane
  // ovog prostora — razrada ostaje jedino mjesto gdje se novi artikl prvi put dodjeljuje prostoru.
  const { data: contractSpaceLinePlansData } = useListSpaceLinePlansByContract(
    workPackage?.contractId ?? '',
    { query: { enabled: !!workPackage?.contractId, staleTime: 0 } }
  );
  const pickableLineIds = useMemo(() => {
    if (!workPackage?.spaceId) return new Set<string>();
    const branchSpaceIds = collectDescendantSpaceIds(objectSpaces, workPackage.spaceId);
    return linesPlannedInBranch(contractSpaceLinePlansData ?? [], branchSpaceIds);
  }, [objectSpaces, workPackage?.spaceId, contractSpaceLinePlansData]);

  // Stvarni strop za dodavanje stavki u OVAJ radni paket — TOČNO ovaj prostor (ne grana), isto
  // ograničenje kao backendov assertWithinSpacePlan: DRAFT-planirana količina za (prostor, stavka)
  // umanjena za ono što je kroz sve radne pakete u tom istom prostoru već stvarno iskorišteno.
  // staleTime 0: potrošnju mijenjaju i drugi radni paketi / korisnici, keš od prije
  // par minuta nudi količinu koju backend više ne prihvaća.
  const { data: contractPlannedItemsData } = useListPlannedItemsByContract(
    workPackage?.contractId ?? '',
    { query: { enabled: !!workPackage?.contractId, staleTime: 0 } }
  );
  const { data: contractWorkPackagesData } = useListWorkPackagesByContract(
    workPackage?.contractId ?? '',
    { query: { enabled: !!workPackage?.contractId, staleTime: 0 } }
  );
  const spaceLinePlanUsageByLineId = useMemo(() => {
    if (!workPackage?.spaceId) return new Map<string, SpaceLinePlanUsage>();
    return computeSpaceLinePlanUsage(
      contractSpaceLinePlansData ?? [],
      contractPlannedItemsData ?? [],
      contractWorkPackagesData ?? [],
      workPackage.spaceId
    );
  }, [
    workPackage?.spaceId,
    contractSpaceLinePlansData,
    contractPlannedItemsData,
    contractWorkPackagesData
  ]);

  // Realizirana količina stavke se uređuje uz isti strop kao pri dodavanju
  // (WorkPackageItemPickerModal) — nikad iznad ugovorene količine te stavke.
  const { data: contractLinesData } = useListContractLines(workPackage?.contractId ?? '', {
    query: { enabled: !!workPackage?.contractId }
  });
  const contractLines: ContractLine[] = contractLinesData ?? [];
  const contractLineById = useMemo(
    () => new Map(contractLines.map((line) => [line.id, line])),
    [contractLines]
  );

  const { data: assigneesData } = useListWorkPackageAssignees(workPackageId, {
    query: { enabled: !!workPackageId }
  });
  const assignees: WorkPackageAssignee[] = assigneesData ?? [];
  const [assigneePickerOpened, setAssigneePickerOpened] = useState(false);
  const [removingAssignee, setRemovingAssignee] = useState<WorkPackageAssignee | undefined>(
    undefined
  );

  const { data: unitsData } = useListUnits();

  // Stavke se ne spremaju same — izmjene planirane količine, nove i uklonjene stavke čekaju u
  // nacrtu dok korisnik ne klikne "Spremi stavke" (jedan zahtjev, jedna transakcija na backendu).
  const draft = useItemsDraft<ContractLine>();
  const [justSaved, setJustSaved] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Strop planirane količine stavke — isti princip kao backendov assertWithinSpacePlan: prostorni
  // plan umanjen za ono što je u ovom prostoru već potrošeno OSTALIM stavkama (zato se vlastita
  // spremljena količina oduzme od potrošenog, inače bi polje samo sebe blokiralo).
  const maxQtyFor = (contractLineId: string | undefined, savedQty: number) => {
    const usage = contractLineId ? spaceLinePlanUsageByLineId.get(contractLineId) : undefined;
    return usage ? usage.ceiling - (usage.consumed - savedQty) : undefined;
  };
  // 0 je samo početna vrijednost nove stavke — planirana količina mora biti ≥ 1 (i backend).
  const qtyError = (value: DraftValue, max: number | undefined) => {
    if (value === '' || value < 1) return t('workPackages.detail.qtyMin');
    if (max != null && value > max) return t('workPackages.detail.qtyMax', { max });
    return null;
  };

  const displayItems: DisplayItem[] = [
    ...plannedItems.map((item): DisplayItem => {
      const itemId = item.id ?? '';
      const savedQty = item.plannedQty ?? 0;
      const plannedQty = draft.valueOf(itemId, 'plannedQty', savedQty);
      const isRemoved = draft.isRemoved(itemId);
      const isChanged = !isRemoved && plannedQty !== savedQty;
      return {
        key: itemId,
        itemId,
        contractLineId: item.contractLineId,
        itemCode: item.itemCode,
        description: item.description,
        unit: item.unit,
        isNew: false,
        isRemoved,
        plannedQty,
        savedQty,
        isChanged,
        // Neizmijenjena spremljena stavka se ne označava — greška samo na onome što se sprema.
        error: isChanged ? qtyError(plannedQty, maxQtyFor(item.contractLineId, savedQty)) : null
      };
    }),
    ...draft.added.map((added): DisplayItem => {
      const plannedQty = added.values.plannedQty ?? 0;
      return {
        key: added.tempId,
        tempId: added.tempId,
        contractLineId: added.meta.id,
        itemCode: added.meta.itemCode,
        description: added.meta.description,
        unit: unitsData?.find((unit) => unit.id === added.meta.unitId)?.code,
        isNew: true,
        isRemoved: false,
        plannedQty,
        // Početna vrijednost iz pickera — polje se označi tek kad je korisnik promijeni.
        savedQty: Number(added.values.initialQty) || 0,
        isChanged: false,
        error: qtyError(plannedQty, maxQtyFor(added.meta.id, 0))
      };
    })
  ];
  const changedCount = displayItems.filter((item) => item.isChanged).length;
  const removedCount = displayItems.filter((item) => item.isRemoved).length;
  const draftSummary: DraftSummary = {
    changed: changedCount,
    added: draft.added.length,
    removed: removedCount,
    errors: displayItems.filter((item) => item.error).length,
    total: changedCount + draft.added.length + removedCount
  };

  // Potrošnja po prostoru uključuje nespremljene promjene ovog paketa — picker inače nudi
  // količinu koju bi spremanje odbilo.
  const draftSpaceUsageByLineId = new Map(
    [...spaceLinePlanUsageByLineId].map(([lineId, value]) => [lineId, { ...value }])
  );
  displayItems.forEach((item) => {
    const lineUsage = item.contractLineId
      ? draftSpaceUsageByLineId.get(item.contractLineId)
      : undefined;
    if (!lineUsage) return;
    const effective = item.isRemoved ? 0 : Number(item.plannedQty) || 0;
    lineUsage.consumed += effective - (item.isNew ? 0 : item.savedQty);
  });

  const setItemQty = (item: DisplayItem, value: DraftValue) => {
    setJustSaved(false);
    if (item.tempId) draft.setAddedValue(item.tempId, 'plannedQty', value);
    else if (item.itemId) draft.setValue(item.itemId, 'plannedQty', value);
  };
  const removeItem = (item: DisplayItem) => {
    setJustSaved(false);
    if (item.tempId) draft.discardAdded(item.tempId);
    else if (item.itemId) draft.toggleRemoved(item.itemId);
  };
  const addContractLines = (items: { line: ContractLine; plannedQty: number }[]) => {
    setJustSaved(false);
    draft.addItems(
      items.map(({ line, plannedQty }) => ({
        meta: line,
        values: { plannedQty, initialQty: plannedQty }
      }))
    );
  };

  const {
    page: itemsPage,
    setPage: setItemsPage,
    pageSize: itemsPageSize,
    setPageSize: setItemsPageSize,
    totalPages: itemsTotalPages,
    pageItems: pageDisplayItems
  } = useLocalPagination(displayItems);

  const savePlannedItemsMutation = useSavePlannedItems({
    mutation: { meta: { skipGlobalErrorToast: true } }
  });

  const saveDraft = async (): Promise<boolean> => {
    if (!workPackageId || draftSummary.errors > 0) return false;
    setSaveError(null);
    try {
      await savePlannedItemsMutation.mutateAsync({
        id: workPackageId,
        data: {
          removedItemIds: displayItems
            .filter((item) => item.isRemoved && item.itemId)
            .map((item) => item.itemId as string),
          updatedItems: displayItems
            .filter((item) => item.isChanged && item.itemId)
            .map((item) => ({
              itemId: item.itemId as string,
              plannedQty: Number(item.plannedQty)
            })),
          addedItems: draft.added
            .filter((added) => added.meta.id)
            .map((added) => ({
              contractLineId: added.meta.id as string,
              plannedQty: Number(added.values.plannedQty)
            }))
        }
      });
      // Nacrt se briše tek kad su svježe stavke stigle — inače bi na trenutak bljesnulo staro stanje.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getListPlannedItemsQueryKey(workPackageId) }),
        queryClient.invalidateQueries({ queryKey: getGetWorkPackageQueryKey(workPackageId) }),
        invalidateContractSpaceUsage(queryClient, workPackage?.contractId)
      ]);
      draft.reset();
      setJustSaved(true);
      setSavedAt(
        new Date().toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
      );
      return true;
    } catch (error) {
      setSaveError(
        t('itemsDraft.saveFailed', { reason: workPackageErrorMessage(error, t('common.error')) })
      );
      return false;
    }
  };

  const discardDraft = () => {
    draft.reset();
    setSaveError(null);
  };

  const { guard, modal: unsavedChangesModal } = useUnsavedChangesGuard({
    summary: draftSummary,
    onSave: saveDraft,
    onDiscard: discardDraft
  });

  const removeAssigneeMutation = useRemoveWorkPackageAssignee({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListWorkPackageAssigneesQueryKey(workPackageId)
        });
        setRemovingAssignee(undefined);
      }
    }
  });

  const deleteMutation = useDeleteWorkPackage({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListAllWorkPackagesQueryKey() });
        // Brisanje ovdje kaskadno briše i planirane stavke (vidi backend
        // deleteWorkPackage) — bez ovoga bi druge stranice istog ugovora
        // (npr. novi radni paket u istom prostoru) i dalje vidjele keširan
        // popis koji uključuje ovaj (sad obrisani) paket i njegove stavke,
        // pa bi prostor izgledao "potpuno iskorišten" iako to više nije točno.
        if (workPackage?.contractId) {
          queryClient.invalidateQueries({
            queryKey: getListWorkPackagesByContractQueryKey(workPackage.contractId)
          });
          queryClient.invalidateQueries({
            queryKey: getListPlannedItemsByContractQueryKey(workPackage.contractId)
          });
        }
        navigate('/work-packages', {
          state: { savedMessage: t('workPackages.detail.deleteSuccess') }
        });
      },
      onError: (error) => {
        setDeleteModalOpened(false);
        setActionError(workPackageErrorMessage(error, t('common.error')));
      }
    }
  });

  // Isti mutation hook za oba prijelaza (Nacrt→Aktivan i Aktivan→Nacrt) — jedina razlika je
  // targetirani status i poruka, oboje proslijeđeno pri pozivu (vidi statusActionButton niže).
  const statusMutation = useUpdateWorkPackage({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (updated) => {
        // setQueryData izravno (ne samo invalidate) — odgovor PATCH-a već nosi svježi status,
        // nema razloga čekati dodatni round-trip da se pill/tracker na ovoj stranici ažurira.
        queryClient.setQueryData(getGetWorkPackageQueryKey(workPackageId), updated);
        queryClient.invalidateQueries({ queryKey: getListAllWorkPackagesQueryKey() });
        const updatedStatus = (updated as WorkPackage | undefined)?.status;
        setActionError(null);
        setSavedMessage(
          t(
            updatedStatus === WorkPackageStatus.DRAFT
              ? 'workPackages.detail.revertSuccess'
              : 'workPackages.detail.activateSuccess'
          )
        );
      },
      onError: (error) => {
        setActionError(workPackageErrorMessage(error, t('common.error')));
      }
    }
  });

  if (isLoading) {
    return (
      <Center py={80}>
        <Loader color="var(--color-accent-primary)" />
      </Center>
    );
  }

  if (!workPackage) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('workPackages.detail.notAvailable')}
        </Text>
      </Container>
    );
  }

  const config = statusConfig[workPackage.status ?? 'DRAFT'];
  const trackerSteps = buildWorkPackageTrackerSteps(workPackage.status ?? 'DRAFT');
  // Header/stavke editabilni su SAMO dok je Nacrt — nakon aktivacije jedina dopuštena promjena je
  // povratak statusa natrag u Nacrt (vidi WorkStructureService.assertWorkPackageDraft na backendu).
  const isDraft = (workPackage.status ?? WorkPackageStatus.DRAFT) === WorkPackageStatus.DRAFT;

  const changeStatus = (status: WorkPackageStatus) =>
    statusMutation.mutate({
      id: workPackageId,
      data: {
        code: workPackage.code ?? '',
        name: workPackage.name ?? '',
        description: workPackage.description,
        status: status as UpdateWorkPackageRequestStatus,
        validFrom: workPackage.validFrom ?? undefined,
        validTo: workPackage.validTo ?? undefined,
        sortOrder: workPackage.sortOrder
      }
    });

  const infoFields: InfoField[] = [
    {
      label: t('workPackages.detail.fieldContract'),
      value: contract?.contractNumber ?? contract?.name ?? '—',
      ...(contract?.id ? { onClick: () => navigate(`/contracts/${contract.id}`) } : {})
    },
    ...(workPackage.objectId
      ? [
          {
            label: t('workPackages.detail.fieldObject'),
            value: workPackage.objectName ?? '—',
            onClick: () => navigate(`/templates/object-registry/${workPackage.objectId}`)
          }
        ]
      : []),
    { label: t('workPackages.detail.fieldSpace'), value: spaceBreadcrumb },
    {
      label: t('workPackages.detail.fieldAssignees'),
      value:
        assignees.length > 0
          ? t('workPackages.detail.assigneeCount', { count: assignees.length })
          : t('workPackages.detail.noAssignees')
    },
    {
      label: t('workPackages.detail.fieldValidFrom'),
      value: formatDate(workPackage.validFrom) || '—'
    },
    { label: t('workPackages.detail.fieldValidTo'), value: formatDate(workPackage.validTo) || '—' },
    {
      label: t('workPackages.detail.fieldCreated'),
      value: formatDate(workPackage.createdAt) || '—'
    }
  ];

  // [naslov, širina (undefined = ostatak), poravnanje]
  const itemColumns: [string, number | undefined, 'left' | 'right'][] = [
    [t('workPackages.detail.colItemCode'), 100, 'left'],
    [t('workPackages.detail.colItemName'), undefined, 'left'],
    [t('workPackages.detail.colUnit'), 80, 'left'],
    [t('workPackages.detail.colContracted'), 90, 'right'],
    [t('workPackages.detail.colRecorded'), 90, 'right'],
    [t('workPackages.detail.colRemaining'), 90, 'right'],
    [t('workPackages.detail.colQty'), isDraft ? 120 : 70, 'right'],
    // Uklanjanje stavki samo dok je Nacrt — inače se stupac ne prikazuje.
    ...(isDraft
      ? [
          [t('workPackages.detail.removeItem').toUpperCase(), 60, 'right'] as [
            string,
            number,
            'right'
          ]
        ]
      : [])
  ];

  // Evidentirano = živi zbroj svih aktivnosti na liniji ugovora (recordedQty), ne realizedQty —
  // to je "odobreno" i raste tek odobrenjem tjednog izvještaja.
  const lineFigures = (item: DisplayItem) => {
    const contractLine = item.contractLineId
      ? contractLineById.get(item.contractLineId)
      : undefined;
    return {
      contracted: contractLine?.quantity,
      recorded: contractLine?.recordedQty,
      remaining: contractLine
        ? (contractLine.quantity ?? 0) - (contractLine.recordedQty ?? 0)
        : undefined
    };
  };

  const hasDraftState = (item: DisplayItem) =>
    item.isNew || item.isRemoved || item.isChanged || !!item.error;

  const renderItemAction = (item: DisplayItem) =>
    item.isRemoved ? (
      <UndoRemoveButton onClick={() => removeItem(item)} />
    ) : (
      <Box
        role="button"
        tabIndex={0}
        aria-label={t('workPackages.detail.removeItem')}
        onClick={() => removeItem(item)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') removeItem(item);
        }}
        style={{ cursor: 'pointer', color: 'var(--color-status-danger)' }}
      >
        <IconFigmaXCircle size={16} />
      </Box>
    );

  const renderItemRow = (item: DisplayItem, index: number) => {
    const { contracted, recorded, remaining } = lineFigures(item);
    const mutedText = item.isRemoved ? 'var(--color-text-muted)' : undefined;
    return (
      <div
        key={item.key}
        style={{
          ...tableRowStyle,
          padding: '14px 20px',
          borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
          backgroundColor: isDraft && hasDraftState(item) ? DRAFT_ROW_BACKGROUND : undefined
        }}
      >
        <Text
          fz={13.5}
          fw={600}
          w={100}
          style={{ color: mutedText ?? 'var(--color-text-primary)' }}
        >
          {item.itemCode}
        </Text>
        <Group gap={8} wrap="nowrap" justify="space-between" style={{ flex: 1, minWidth: 0 }}>
          <Text
            fz={13}
            truncate
            style={{
              minWidth: 0,
              color: mutedText ?? 'var(--color-text-secondary)',
              textDecoration: item.isRemoved ? 'line-through' : undefined
            }}
          >
            {item.description}
          </Text>
          {item.isNew && <NewItemBadge />}
        </Group>
        <Text fz={13} w={80} style={{ color: 'var(--color-text-muted)' }}>
          {item.unit}
        </Text>
        <Text fz={13} w={90} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
          {contracted ?? '—'}
        </Text>
        <Text fz={13} w={90} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
          {recorded ?? '—'}
        </Text>
        <Text
          fz={13}
          fw={600}
          w={90}
          ta="right"
          style={{
            color:
              remaining != null && remaining < 0
                ? 'var(--color-status-danger)'
                : 'var(--color-text-primary)'
          }}
        >
          {remaining ?? '—'}
        </Text>
        {isDraft ? (
          <>
            <div style={{ width: 120, display: 'flex', justifyContent: 'flex-end' }}>
              <DraftNumberInput
                width={70}
                value={item.plannedQty}
                savedValue={item.savedQty}
                isNew={item.isNew}
                error={item.error}
                disabled={item.isRemoved}
                ariaLabel={t('workPackages.detail.colQty')}
                onChange={(value) => setItemQty(item, value)}
              />
            </div>
            <Group w={60} justify="flex-end">
              {renderItemAction(item)}
            </Group>
          </>
        ) : (
          <Text fz={13} fw={600} w={70} ta="right" style={{ color: 'var(--color-text-primary)' }}>
            {item.plannedQty}
          </Text>
        )}
      </div>
    );
  };

  const renderItemCard = (item: DisplayItem) => {
    const { contracted, recorded, remaining } = lineFigures(item);
    const figure = (label: string, value: number | undefined) => (
      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
        {label}{' '}
        <Text span fz={11} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {value ?? '—'}
        </Text>
      </Text>
    );
    return (
      <Stack
        key={item.key}
        gap={10}
        p={16}
        style={{
          borderRadius: 12,
          border: '1px solid var(--color-border-soft)',
          backgroundColor: hasDraftState(item) ? DRAFT_ROW_BACKGROUND : 'var(--color-bg-surface)'
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={8} wrap="nowrap">
            <Text fz={12.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {t('contractDetail.cardCode', { code: item.itemCode ?? '—' })}
            </Text>
            {item.isNew && <NewItemBadge />}
          </Group>
          {renderItemAction(item)}
        </Group>
        <Text
          fz={13.5}
          style={{
            color: item.isRemoved ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
            textDecoration: item.isRemoved ? 'line-through' : undefined
          }}
        >
          {item.description}
        </Text>
        <Group gap={12} wrap="wrap">
          {figure(t('workPackages.detail.cardContracted'), contracted)}
          {figure(t('workPackages.detail.cardRecorded'), recorded)}
          {figure(t('workPackages.detail.cardRemaining'), remaining)}
        </Group>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Text fz={13} pt={10} style={{ color: 'var(--color-text-secondary)' }}>
            {t('workPackages.detail.cardPlannedQty', { unit: item.unit ?? '' })}
          </Text>
          <DraftNumberInput
            width={80}
            height={40}
            value={item.plannedQty}
            savedValue={item.savedQty}
            isNew={item.isNew}
            error={item.error}
            disabled={item.isRemoved}
            ariaLabel={t('workPackages.detail.colQty')}
            onChange={(value) => setItemQty(item, value)}
          />
        </Group>
      </Stack>
    );
  };

  const headerActions = (
    <>
      {isDraft ? (
        <MutationButton
          isPending={statusMutation.isPending}
          onClick={() => guard(() => changeStatus(WorkPackageStatus.ACTIVE))}
        >
          {t('workPackages.detail.activateAction')}
        </MutationButton>
      ) : (
        <MutationButton
          variant="outline"
          isPending={statusMutation.isPending}
          onClick={() => changeStatus(WorkPackageStatus.DRAFT)}
        >
          {t('workPackages.detail.revertAction')}
        </MutationButton>
      )}
      {isDraft && (
        <Button variant="outline" onClick={() => navigate(`/work-packages/${workPackageId}/edit`)}>
          {t('workPackages.detail.editButton')}
        </Button>
      )}
      <Button color="red" variant="outline" onClick={() => guard(() => setDeleteModalOpened(true))}>
        {t('workPackages.detail.deleteAction')}
      </Button>
    </>
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/work-packages" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        {actionError && (
          <StatusBanner
            tone="danger"
            message={actionError}
            onDismiss={() => setActionError(null)}
          />
        )}

        {saveError && (
          <StatusBanner tone="danger" message={saveError} onDismiss={() => setSaveError(null)} />
        )}

        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={6}>
            <Group gap={12} align="center">
              <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
                {workPackage.name}
              </Title>
              <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} header />
            </Group>
            <Text fz={14} style={{ color: 'var(--color-text-muted)' }}>
              {workPackage.code}
            </Text>
          </Stack>
          <Group gap={12} visibleFrom="sm">
            {headerActions}
          </Group>
          <MobileActionBar>{headerActions}</MobileActionBar>
        </Group>

        <StatusTracker steps={trackerSteps} />

        <InfoCard
          title={t('workPackages.detail.basicInfoTitle')}
          fields={infoFields}
          columns={4}
          borderColor="var(--color-border-default)"
          trailing={
            workPackage.description ? (
              <Stack gap={4}>
                <Text
                  fz={11}
                  fw={600}
                  tt="uppercase"
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t('workPackages.detail.fieldDescription')}
                </Text>
                <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
                  {workPackage.description}
                </Text>
              </Stack>
            ) : undefined
          }
        />

        <CardSection
          title={t('workPackages.detail.assigneesTitle')}
          headerRight={
            <Text
              fz={13}
              fw={600}
              onClick={() => setAssigneePickerOpened(true)}
              style={{ cursor: 'pointer', color: 'var(--color-accent-primary-active)' }}
            >
              + {t('workPackages.detail.addAssignee')}
            </Text>
          }
        >
          {assignees.length === 0 ? (
            <Stack align="center" gap={4} py={32}>
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                {t('workPackages.detail.assigneesEmptyTitle')}
              </Text>
              <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                {t('workPackages.detail.assigneesEmptyHint')}
              </Text>
            </Stack>
          ) : (
            <Stack gap={0}>
              {assignees.map((assignee, index) => (
                <div
                  key={assignee.id}
                  style={{
                    ...tableRowStyle,
                    padding: '14px 20px',
                    borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                  }}
                >
                  <Text fz={13.5} fw={600} style={{ flex: 1, color: 'var(--color-text-primary)' }}>
                    {assignee.teamId
                      ? assignee.teamName
                      : `${assignee.userFirstName ?? ''} ${assignee.userLastName ?? ''}`.trim()}
                  </Text>
                  <Text fz={12} w={120} style={{ color: 'var(--color-text-muted)' }}>
                    {assignee.teamId
                      ? t('workPackages.detail.assigneeTypeTeam')
                      : (assignee.userEmail ?? t('workPackages.detail.assigneeTypeIndividual'))}
                  </Text>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setRemovingAssignee(assignee)}
                    style={{ cursor: 'pointer', color: 'var(--color-status-danger)' }}
                  >
                    <IconFigmaXCircle size={16} />
                  </Box>
                </div>
              ))}
            </Stack>
          )}
        </CardSection>

        <CardSection
          title={t('workPackages.detail.itemsTitle')}
          titleInfo={
            <PanelInfoIcon
              width={360}
              title={t('workPackages.detail.itemsTitle')}
              label={
                <InfoBulletList
                  intro={t('columnsInfo.intro')}
                  items={[
                    t('columnsInfo.contracted'),
                    t('columnsInfo.recorded'),
                    t('columnsInfo.remaining'),
                    t('columnsInfo.plannedQty')
                  ]}
                />
              }
            />
          }
          headerRight={
            isDraft && (
              <Group gap={16} wrap="nowrap">
                <ItemsSaveStatus summary={draftSummary} justSaved={justSaved} savedAt={savedAt} />
                <Button size="sm" variant="outline" onClick={() => setPickerOpened(true)}>
                  + {t('workPackages.detail.addItems')}
                </Button>
              </Group>
            )
          }
        >
          {displayItems.length === 0 ? (
            <Stack align="center" gap={4} py={32}>
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                {t('workPackages.detail.itemsEmptyTitle')}
              </Text>
              <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                {t('workPackages.detail.itemsEmptyHint')}
              </Text>
            </Stack>
          ) : (
            <Stack gap={0}>
              {/* Na mobitelu se stavke Nacrta uređuju na karticama (ispod); tablica ostaje za pregled. */}
              <Box visibleFrom={isDraft ? 'sm' : undefined}>
                <div
                  className="table-header-row"
                  style={{
                    ...tableRowStyle,
                    padding: '12px 20px',
                    backgroundColor: 'var(--color-bg-canvas)',
                    borderBottom: '1px solid var(--color-border-soft)'
                  }}
                >
                  {itemColumns.map(([label, width, align]) => (
                    <Text
                      key={label}
                      fz={10}
                      fw={600}
                      w={width}
                      ta={align}
                      style={{
                        flex: width ? undefined : 1,
                        color: 'var(--color-text-muted)',
                        letterSpacing: 0.4
                      }}
                    >
                      {label}
                    </Text>
                  ))}
                </div>
                {pageDisplayItems.map((item, index) => renderItemRow(item, index))}
                {isDraft && (
                  <ItemsSaveBar
                    summary={draftSummary}
                    isSaving={savePlannedItemsMutation.isPending}
                    onCancel={discardDraft}
                    onSave={saveDraft}
                  />
                )}
              </Box>
              {isDraft && (
                <Stack hiddenFrom="sm" gap={12} p={16}>
                  {pageDisplayItems.map((item) => renderItemCard(item))}
                </Stack>
              )}
              <ListPagination
                label={t('workPackages.detail.itemsShowing', {
                  shown: pageDisplayItems.length,
                  total: displayItems.length
                })}
                page={itemsPage}
                totalPages={itemsTotalPages}
                onPageChange={setItemsPage}
                pageSize={itemsPageSize}
                onPageSizeChange={setItemsPageSize}
              />
            </Stack>
          )}
        </CardSection>
      </Stack>

      <WorkPackageItemPickerModal
        opened={pickerOpened}
        onClose={() => setPickerOpened(false)}
        contractId={workPackage.contractId}
        existingContractLineIds={displayItems
          .map((item) => item.contractLineId)
          .filter((value): value is string => !!value)}
        restrictToLineIds={pickableLineIds}
        spaceLinePlanUsageByLineId={draftSpaceUsageByLineId}
        onAdd={addContractLines}
      />

      <ConfirmActionModal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        onConfirm={() => deleteMutation.mutate({ id: workPackageId })}
        loading={deleteMutation.isPending}
        isPaused={deleteMutation.isPaused}
        danger
        title={t('workPackages.detail.deleteTitle')}
        message={t('workPackages.detail.deleteMessage', { name: workPackage?.name ?? '' })}
        confirmLabel={t('workPackages.detail.deleteConfirm')}
      />

      <WorkPackageAssigneePickerModal
        opened={assigneePickerOpened}
        onClose={() => setAssigneePickerOpened(false)}
        workPackageId={workPackageId}
        existingTeamIds={assignees
          .map((assignee) => assignee.teamId)
          .filter((value): value is string => !!value)}
        existingUserIds={assignees
          .map((assignee) => assignee.userId)
          .filter((value): value is string => !!value)}
      />

      <ConfirmActionModal
        opened={!!removingAssignee}
        onClose={() => setRemovingAssignee(undefined)}
        onConfirm={() =>
          removingAssignee?.id &&
          removeAssigneeMutation.mutate({ id: workPackageId, assigneeId: removingAssignee.id })
        }
        loading={removeAssigneeMutation.isPending}
        isPaused={removeAssigneeMutation.isPaused}
        danger
        title={t('workPackages.detail.removeAssignee')}
        message={
          removingAssignee?.teamId
            ? (removingAssignee?.teamName ?? '')
            : `${removingAssignee?.userFirstName ?? ''} ${removingAssignee?.userLastName ?? ''}`.trim()
        }
        confirmLabel={t('workPackages.detail.removeAssignee')}
      />

      {isDraft && (
        <Box hiddenFrom="sm">
          <ItemsSaveBar
            variant="mobile"
            summary={draftSummary}
            isSaving={savePlannedItemsMutation.isPending}
            onCancel={discardDraft}
            onSave={saveDraft}
          />
        </Box>
      )}

      {unsavedChangesModal}
    </Container>
  );
};
